
import Vue from 'vue'

import {
  ACCOUNT_IS_READY_ON_CREATION,
  DISABLING_REMOVES_OPEN_SESSIONS,
  ManagedAccount,
  NEW_PASSWORD_MAX_LENGTH,
  NEW_PASSWORD_MIN_LENGTH,
  RE_ENABLING_DOES_NOT_RESTORE_SESSIONS,
  SELF_REFUSAL,
  UpdateFields,
  createAccount,
  createdNotice,
  loadAccounts,
  isOwnAccount,
  loadAccount,
  newAccountFormAfter,
  resetPassword,
  selfProtectedFields,
  updateAccount,
  updateFieldsFor
} from './../adminusers'
import { store } from './../store'
import { client } from './../backend/client'

// The screen the guarded administrative route leads to, and the only place an
// account comes into existence -- there is no sign-up.
//
// **Everything it decides is decided in `adminusers.ts`**, which is where this
// screen's tests live. That is the spec's decision and it is not a shortcut:
// components are never mounted, so a `.vue` file is presentation and nothing
// else. The two things worth saying about it live here for exactly that reason.
//
// The copy is not decoration. Two of this ticket's criteria are explicitly about
// what the interface *says* -- that disabling ends sessions already open, and
// that re-enabling does not bring them back -- and both are asserted in
// `adminusers.spec.ts` against the module's constants rather than against this
// template. A string in a template is a string nothing tests.
//
// The self-protection is likewise the client half of a rule the server already
// enforces. `admin_update_changeset/3` refuses a request from the acting admin
// about that admin's own active state or admin rights, and proves the refusal is
// about self by asserting the identical request against a second admin succeeds.
// So the switches are disabled and the explanation is shown -- and the button
// stays live for everything else, because an admin correcting their own name and
// email is ordinary and allowed. A client that only hid the controls would offer
// a button that could still be pressed with a planted request; one that relied on
// the server alone would offer a control that always fails.
export default Vue.extend({
  name: 'AdminUsers',

  data () {
    return {
      ACCOUNT_IS_READY_ON_CREATION,
      DISABLING_REMOVES_OPEN_SESSIONS,
      RE_ENABLING_DOES_NOT_RESTORE_SESSIONS,
      SELF_REFUSAL,
      NEW_PASSWORD_MIN_LENGTH,
      NEW_PASSWORD_MAX_LENGTH,
      accounts: [] as ManagedAccount[],
      selected: null as ManagedAccount | null,
      update: { name: '', email: '', active: true, admin: false } as UpdateFields,
      resetPasswordValue: '',
      create: newAccountFormAfter({
        name: '',
        username: '',
        email: '',
        password: '',
        admin: false
      }, false),
      created: '',
      refusal: ''
    }
  },

  computed: {
    protectedFields () {
      if (!this.selected) {
        return { active: false, admin: false }
      }

      return selfProtectedFields(isOwnAccount(store.state.user?.id, this.selected.id))
    }
  },

  async mounted () {
    await this.load()
  },

  methods: {
    async load () {
      try {
        this.accounts = await loadAccounts()
      } catch (error) {
        this.refusal = 'Could not read the accounts. Please try again.'
      }
    },

    async read (id: string) {
      try {
        const account = await loadAccount(id)
        this.selected = account
        this.update = updateFieldsFor(account)
      } catch (error) {
        this.refusal = 'Could not read that account. Please try again.'
      }
    },

    async save () {
      if (!this.selected) {
        return
      }

      try {
        const saved = await updateAccount(this.selected.id, this.update)
        this.selected = saved
        this.accounts = await loadAccounts()
        this.refusal = ''
      } catch (error) {
        this.refusal = 'Saving the account did not work. Please try again.'
      }
    },

    async resetPassword () {
      if (!this.selected) {
        return
      }

      try {
        const saved = await resetPassword(this.selected.id, this.resetPasswordValue)
        this.selected = saved
        this.update = updateFieldsFor(saved)
        this.resetPasswordValue = ''
      } catch (error) {
        this.refusal = 'Resetting the password did not work. Please try again.'
      }
    },

    async createOne () {
      try {
        const account = await createAccount(this.create)
        this.created = createdNotice(account)
        this.refusal = ''
        this.create = newAccountFormAfter(this.create, true)
        this.accounts = await loadAccounts()
      } catch (error) {
        this.created = ''
        this.refusal = 'Creating the account did not work. Please try again.'
      }
    }
  }
})
